import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface ITomraMachineRevenueHistory {
  startDate: Date;
  endDate?: Date | null;
  amount: number;
}
export interface ITomraMachine {
  _id?: string;
  name?: string;
  serialNumber?: string;
  droppie_revenue_history?: ITomraMachineRevenueHistory[];
  location?: string;
  status?: "available" | "not_available";
  unavailableUntil?: string | null;
  showUnavailableUntilTime?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  tomraMachine: ITomraMachine | null;
  hasNextPage: boolean;
  allTomraMachines: ITomraMachine[];
  addedTomraMachine?: string | null;
  updatedTomraMachine?: string | null;
  /** Callbacks */
  getTomraMachines: (page?: number, limit?: number) => void;
  getTomraMachine: (id: string) => void;
  addTomraMachine: (data: ITomraMachine) => void;
  deleteTomraMachine: (id: string) => void;
  updateTomraMachine: (
    id: string,
    data: {
      name: string;
      location: string;
      serialNumber: string;
      droppie_revenue_month?: number;
      status?: "available" | "not_available";
      unavailableUntil?: string | null;
    }
  ) => void;
  addRevenueHistory: (
    machineId: string,
    data: ITomraMachineRevenueHistory
  ) => void;
  updateRevenueHistory: (
    machineId: string,
    index: number,
    data: ITomraMachineRevenueHistory
  ) => void;
  deleteRevenueHistory: (machineId: string, index: number) => void;
  clearAddedTomraMachine: () => void;
  clearUpdatedTomraMachine: () => void;
}

export const useTomraMachines = create<IStore>()((set) => ({
  /** State */
  allTomraMachines: [],
  hasNextPage: false,
  tomraMachine: null,
  addedTomraMachine: null,
  updatedTomraMachine: null,
  /** Callbacks */
  getTomraMachines: async (page = 1, limit = 20) => {
    try {
      const response = await axios.get(
        `tomra-machines?page=${page}&limit=${limit}`
      );
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        allTomraMachines:
          page === 1
            ? response?.data
            : [...state.allTomraMachines, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Tomra Machines", { error });
    }
  },
  getTomraMachine: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`tomra-machines/${id}`);
      set({ tomraMachine: response.data });
    } catch (error) {
      LOG.error("Error on fetching Tomra Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addTomraMachine: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("tomra-machines", body);
      if (response?.data) {
        const newTomraMachine = response.data;
        set((state) => ({
          allTomraMachines: [newTomraMachine, ...state.allTomraMachines],
          addedTomraMachine: newTomraMachine._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Tomra Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteTomraMachine: async (sProductId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`tomra-machines/${sProductId}`);
      set((state) => ({
        allTomraMachines: state.allTomraMachines.filter(
          (sProduct) => sProduct._id !== sProductId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Tomra Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateTomraMachine: async (sProductId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`tomra-machines/${sProductId}`, body);
      if (response?.data) {
        const updatedSProduct = response.data;
        set((state) => ({
          allTomraMachines: state.allTomraMachines.map((sProduct) => {
            if (sProduct._id === updatedSProduct._id) {
              return updatedSProduct;
            }
            return sProduct;
          }),
          updatedTomraMachine: updatedSProduct._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addRevenueHistory: async (machineId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(
        `tomra-machines/${machineId}/revenue-history`,
        body
      );
      if (response?.data) {
        set({ tomraMachine: response?.data });
      }
    } catch (error) {
      LOG.error("Error on adding new Revenue History", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateRevenueHistory: async (machineId, index, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(
        `tomra-machines/${machineId}/revenue-history/${index}`,
        body
      );
      if (response?.data) {
        set({ tomraMachine: response?.data });
      }
    } catch (error) {
      LOG.error("Error on updating Revenue History", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteRevenueHistory: async (machineId, index) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(
        `tomra-machines/${machineId}/revenue-history/${index}`
      );
      if (response?.data) {
        set({ tomraMachine: response?.data });
      }
    } catch (error) {
      LOG.error("Error on deleting Revenue History", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedTomraMachine: () => {
    set({ addedTomraMachine: null });
  },
  clearUpdatedTomraMachine: () => {
    set({ updatedTomraMachine: null });
  },
}));
